// Statistics → Label doctor (admin) — who fixes label refusals: the system (contact repair
// reusing what we know about the customer), the Label Doctor, or a person — by field, by
// carrier, by day. Fernando (2026-09-16): "before we activate the Label Doctor I want to
// track its efficiency". Definitions come from the API so the page and the numbers agree.
import React, { useCallback, useEffect, useState } from 'react';
import { CARRIER_NAME } from '../carriers';
import { useNavigate } from 'react-router-dom';
import { authFetch, API } from '../api';
import { DatePreset, DATE_PRESETS, KPICard, weekRange, monthRange, last30Range, allTimeRange } from './shared';
import { fmtWh } from '../time';

// What the Doctor is doing RIGHT NOW (Fernando, 2026-09-24: "is there any indication of when
// the Doctor is working on something?"). Written by the Doctor after every step; polled here.
interface LiveStep { at: string; text: string }
interface Live {
  state: 'working' | 'idle';
  order_id?: string; order_number?: string; started_at?: string; try?: number; of?: number;
  steps?: LiveStep[]; at?: string; updated_at?: string;
  last?: { order_number?: string; outcome?: string; action?: string; steps?: number } | null;
}
function LiveDoctorCard() {
  const [live, setLive] = useState<Live | null>(null);
  const navigate = useNavigate();
  useEffect(() => {
    let on = true;
    const tick = () => authFetch(`${API}/label-doctor/live`).then(r => r.ok ? r.json() : null)
      .then(d => { if (on && d) setLive(d); }).catch(() => {});
    tick();
    const t = setInterval(tick, 10000);
    return () => { on = false; clearInterval(t); };
  }, []);
  if (!live) return null;
  if (live.state === 'working') return (
    <div className="mb-5 rounded-lg border border-sky-800/60 bg-sky-950/40 px-4 py-3 text-sm text-sky-100">
      <div>
        <span className="font-semibold">🩺 Working now on </span>
        <button onClick={() => live.order_id && navigate(`/orders/${live.order_id}`)}
          className="font-semibold underline decoration-dotted">{live.order_number}</button>
        <span className="text-sky-300/80"> — since {fmtWh(live.started_at, 'time')}, try {live.try} of {live.of}</span>
      </div>
      {(live.steps || []).slice(-8).map((st, i) => (
        <div key={i} className="text-xs text-sky-300/80 truncate">{fmtWh(st.at, 'seconds')} · {st.text}</div>
      ))}
    </div>
  );
  return (
    <div className="mb-5 rounded-lg border border-gray-800 bg-gray-900/60 px-4 py-2.5 text-sm text-gray-400">
      🩺 Idle — checks for failed labels every 5 minutes.
      {live.last?.order_number && (
        <span> Last: <span className="text-gray-200">{live.last.order_number}</span>
          {' '}→ {live.last.outcome}{live.last.action ? ` / ${live.last.action}` : ''}
          {live.at ? ` (${fmtWh(live.at, 'time')})` : ''}</span>
      )}
    </div>
  );
}

interface Bucket {
  refusals: number; system: number; label_doctor: number; doctor_proposed_human_applied: number;
  human: number; formatting: number; open: number; not_data: number;
  median_hours: Record<string, number | null>; auto_share: number | null;
}
interface Payload {
  from: string; to: string;
  kpis: Bucket;
  /** the carrier's API was down — one case per parcel and outage; not fixes */
  system_issues?: { cases: number; came_through: number; still_failing: number; retries: number;
                    by_carrier: Record<string, { cases: number; came_through: number }> };
  /** our own configuration blocked the label */
  setup_issues?: { cases: number; resolved: number; open: number; by_kind: Record<string, number>;
                   recent: { order_id: string; order_number: string; carrier: string; kind: string;
                             message: string; at: string; resolved: boolean }[] };
  /** Label Doctor rule 9 (2026-10-16): phone numbers corrected as ONE typing slip, and what became of them */
  typo_fixes?: { rows: { order_id: string; order_number: string; store: string; country: string; given: string;
                         applied: string; slip: string; fixed_at: string; outcome: string; outcome_text: string;
                         delivered_at: string | null }[];
                 counts: Record<string, number>; labels: Record<string, string> };
  funnel: { total: number; labels_bought: number; natural: number; refused: number; refused_attempts: number;
            system: number; label_doctor: number; human: number; open: number; not_data: number;
            pct: Record<string, number | null> };
  by_field: (Bucket & { field: string })[];
  by_carrier: (Bucket & { carrier: string })[];
  daily: (Bucket & { date: string })[];
  recent: { order_id: string; order_number: string; store: string; carrier: string; path: string; field: string;
            message: string; at: string; resolved_by: string; hours: number | null; used_source: string | null;
            tracking: string | null }[];
  doctor: { diagnoses: number; outcomes: Record<string, number>; resolutions: Record<string, number>;
            agreement: Record<string, { compared: number; matched: number }>;
            agreement_total: { compared: number; matched: number };
            tokens: { input: number; output: number; per_diagnosis: number };
            median_hours_to_label: number | null; mode: string;
            applied: { total: number; retry_unchanged: number; apply_and_retry: number; bought: number; refused: number;
                       pending: number; fields: Record<string, number> } };
  definitions: Record<string, string>;
}

const FIELD_LABEL: Record<string, string> = {
  phone: 'Phone', email: 'E-mail', postal_code: 'Zip / postal code', city: 'City', street_address: 'Street',
  house_number: 'House number', pickup_point: 'Pickup point', config: 'Configuration', unshippable: 'Carrier restriction',
  other: 'Other',
};
const WHO_LABEL: Record<string, string> = {
  system: 'System', label_doctor: 'Label Doctor', doctor_proposed_human_applied: 'Doctor proposed · person applied',
  human: 'Person', formatting: 'Formatting', open: 'Still open', not_data: 'Not a data fix',
};
const WHO_COLOR: Record<string, string> = {
  system: 'text-green-400', label_doctor: 'text-purple-300', doctor_proposed_human_applied: 'text-purple-400',
  human: 'text-blue-300', formatting: 'text-teal-300', open: 'text-amber-400', not_data: 'text-gray-500',
};
const CARRIER = CARRIER_NAME;   // the one frontend copy (carriers.tsx)
const hrs = (h: number | null | undefined) => h == null ? '—' : h < 1 ? `${Math.round(h * 60)} min` : `${h.toFixed(1)} h`;

function Cols({ b, hideOpen }: { b: Bucket; hideOpen?: boolean }) {
  const cell = (n: number, who: string) => (
    <td className={`px-3 py-2 text-right tabular-nums ${n ? WHO_COLOR[who] : 'text-gray-700'}`}>{n}</td>
  );
  return (
    <>
      <td className="px-3 py-2 text-right tabular-nums text-white font-semibold">{b.refusals}</td>
      {cell(b.system, 'system')}
      {cell(b.label_doctor, 'label_doctor')}
      {cell(b.doctor_proposed_human_applied, 'doctor_proposed_human_applied')}
      {cell(b.human, 'human')}
      {cell(b.formatting, 'formatting')}
      {!hideOpen && cell(b.open, 'open')}
      <td className="px-3 py-2 text-right tabular-nums text-gray-300">{b.auto_share == null ? '—' : `${b.auto_share}%`}</td>
    </>
  );
}
function Head({ first, hideOpen }: { first: string; hideOpen?: boolean }) {
  const th = (t: string) => <th className="px-3 py-2 text-right font-medium whitespace-nowrap">{t}</th>;
  return (
    <thead><tr className="text-gray-500 text-xs border-b border-gray-800">
      <th className="px-3 py-2 text-left font-medium">{first}</th>
      {th('Refusals')}{th('System')}{th('Label Doctor')}{th('Doctor → person')}{th('Person')}{th('Formatting')}
      {!hideOpen && th('Open')}{th('Auto %')}
    </tr></thead>
  );
}

export default function LabelDoctorStats() {
  const navigate = useNavigate();
  const role = localStorage.getItem('wms_role') || 'staff';
  const [preset, setPreset] = useState<DatePreset>('last30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const getRange = useCallback(() => {
    if (preset === 'week') return weekRange();
    if (preset === 'month') return monthRange();
    if (preset === 'all') return allTimeRange();
    if (preset === 'custom') return { from: customFrom, to: customTo };
    return last30Range();
  }, [preset, customFrom, customTo]);

  const load = useCallback(async () => {
    const { from, to } = getRange();
    if (!from || !to) return;
    setLoading(true); setErr('');
    try {
      const r = await authFetch(`${API}/statistics/label-doctor?from_date=${from}&to_date=${to}`);
      const d = await r.json();
      if (!r.ok) { setErr(d.detail || 'Could not load'); return; }
      setData(d);
    } catch { setErr('Could not reach the WMS'); }
    finally { setLoading(false); }
  }, [getRange]);
  useEffect(() => { load(); }, [load]);

  if (role !== 'admin') return <div className="p-6 text-gray-400 text-sm">Label doctor statistics are available to admins only.</div>;

  const presets = DATE_PRESETS;
  const k = data?.kpis;
  const f = data?.funnel;
  const pc = (key: string) => { const v = f?.pct?.[key]; return v == null ? '—' : `${v}%`; };
  const nv = (n: number, key: string) => `${n} · ${pc(key)}`;
  const maxDaily = Math.max(1, ...(data?.daily.map(d => d.refusals) || [1]));

  return (
    <div className="p-6 max-w-7xl">
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white">Label doctor</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Who fixes label refusals — the system reusing what we know about the customer, the Label Doctor, or a person — by field and by carrier.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {presets.map(p => (
            <button key={p.key} onClick={() => setPreset(p.key)}
              className={`px-3 py-1.5 text-xs rounded-lg ${preset === p.key ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
              {p.label}
            </button>
          ))}
          {preset === 'custom' && (
            <>
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="bg-gray-800 border border-gray-700 text-white text-xs rounded-lg px-2 py-1.5" />
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="bg-gray-800 border border-gray-700 text-white text-xs rounded-lg px-2 py-1.5" />
            </>
          )}
        </div>
      </div>
      <LiveDoctorCard />

      {err && <div className="text-red-400 text-sm bg-red-900/20 border border-red-800/40 rounded-lg px-4 py-2 mb-4">{err}</div>}
      {loading && !data && <div className="text-gray-500 text-sm">Loading…</div>}

      {k && f && data && (
        <>
          {/* The funnel: total → natural → refused → who fixed it. Per order, shares of total. */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-2">
            <KPICard label="Total shipments" value={String(f.total)}
              sub={`${f.labels_bought} labels bought · ${f.open} refused, no label yet`} />
            <KPICard label="Succeeded naturally" value={nv(f.natural, 'natural')} color="green"
              sub="label bought without any refusal" />
            <KPICard label="Label issues" value={nv(f.refused, 'refused')} color={f.refused ? 'yellow' : undefined}
              sub="parcels with something actually wrong in their data" />
            <KPICard label="Fixed by the system" value={nv(f.system, 'system')} color={f.system ? 'green' : undefined}
              sub={`${k.system} from the customer's history · ${k.formatting} by formatting · median ${hrs(k.median_hours.system)}`} />
            <KPICard label="Fixed by the Label Doctor" value={nv(f.label_doctor, 'label_doctor')}
              color={f.label_doctor ? 'green' : undefined}
              sub={`${k.doctor_proposed_human_applied} proposed by the Doctor, applied by a person · mode ${data.doctor.mode}`} />
            <KPICard label="Fixed by a person" value={nv(f.human, 'human')} color={f.open ? 'yellow' : undefined}
              sub={`median ${hrs(k.median_hours.human)} · ${f.open} still open (${pc('open')})`} />
          </div>
          <p className="text-gray-600 text-xs mb-6">
            Counted per parcel: a label issue is something actually wrong with the parcel's data (postcode, phone, e-mail, street,
            house number, city, pickup point), counted once however many tries it took. Fixed automatically:{' '}
            <span className="text-gray-300">{k.auto_share == null ? '—' : `${k.auto_share}%`}</span>
            {' '}(system + Doctor + formatting). Carrier outages and our own settings are not label issues — see below. Recorded since 31 Aug 2026.
          </p>

          {/* By field */}
          <Section title="Label issues by field" note="What was wrong with the parcel's data, and who fixed it. A parcel wrong in two fields counts in both rows.">
            <table className="w-full text-sm">
              <Head first="Field" />
              <tbody>
                {data.by_field.length === 0 && <tr><td colSpan={10} className="px-3 py-8 text-center text-gray-600">No refusals in this period</td></tr>}
                {data.by_field.map(r => (
                  <tr key={r.field} className="border-b border-gray-800/50 hover:bg-gray-800/20">
                    <td className="px-3 py-2 text-white">{FIELD_LABEL[r.field] || r.field}</td>
                    <Cols b={r} />
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* By carrier */}
          <Section title="Label issues by carrier" note="Which carrier refused the parcel's data, and who fixed it.">
            <table className="w-full text-sm">
              <Head first="Carrier" />
              <tbody>
                {data.by_carrier.map(r => (
                  <tr key={r.carrier} className="border-b border-gray-800/50 hover:bg-gray-800/20">
                    <td className="px-3 py-2 text-white">{CARRIER[r.carrier] || r.carrier}</td>
                    <Cols b={r} />
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* System issues: the carrier's API — cases, never fixes */}
          {data.system_issues && (
            <Section title="System issues — carrier APIs"
              note="The carrier's API was down or failed on its side. One case per parcel and outage however many retries (the WMS retries every 30 minutes); not a label issue and not a fix — it shows how reliable each carrier's API is.">
              <div className="px-4 py-3 text-sm text-gray-300 flex gap-6 flex-wrap">
                <span><span className="text-white font-semibold">{data.system_issues.cases}</span> outage cases</span>
                <span><span className="text-green-400 font-semibold">{data.system_issues.came_through}</span> came through on a retry</span>
                <span><span className={data.system_issues.still_failing ? 'text-amber-400 font-semibold' : 'text-gray-500'}>{data.system_issues.still_failing}</span> still failing</span>
                <span className="text-gray-500">{data.system_issues.retries} retries not counted separately</span>
              </div>
              {Object.keys(data.system_issues.by_carrier).length > 0 && (
                <table className="w-full text-sm">
                  <thead><tr className="text-gray-500 text-xs border-b border-gray-800">
                    <th className="px-3 py-2 text-left font-medium">Carrier</th>
                    <th className="px-3 py-2 text-right font-medium">Outage cases</th>
                    <th className="px-3 py-2 text-right font-medium">Came through</th>
                  </tr></thead>
                  <tbody>
                    {Object.entries(data.system_issues.by_carrier).sort((a, b) => b[1].cases - a[1].cases).map(([c, v]) => (
                      <tr key={c} className="border-b border-gray-800/50">
                        <td className="px-3 py-2 text-white">{CARRIER[c] || c}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-white">{v.cases}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-green-400">{v.came_through}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>
          )}

          {/* Rule 9: the Doctor's phone typo fixes — Fernando decides on these numbers whether to keep them */}
          {data.typo_fixes && (
            <Section title="Phone typo fixes — rule 9 (measured to decide)"
              note="The Label Doctor corrected the customer's phone by ONE typing slip (SI1033: +686… → +386…), without asking. Each fix is followed to its end: was the label bought, was the parcel delivered. Fernando, 16.10: decide on these numbers whether to keep the rule.">
              <div className="px-4 py-3 text-sm text-gray-300 flex gap-6 flex-wrap">
                <span><span className="text-white font-semibold">{data.typo_fixes.rows.length}</span> typo fixes</span>
                {Object.entries(data.typo_fixes.labels).map(([k, label]) => (
                  <span key={k} className={data.typo_fixes!.counts[k] ? (k === 'delivered' ? 'text-green-400' : k === 'returned' || k === 'refused_again' ? 'text-red-400' : 'text-gray-300') : 'text-gray-600'}>
                    {label}: {data.typo_fixes!.counts[k] || 0}</span>
                ))}
              </div>
              {data.typo_fixes.rows.length > 0 ? (
                <table className="w-full text-sm">
                  <tbody>
                    {data.typo_fixes.rows.map((r, i) => (
                      <tr key={i} className="border-t border-gray-800/50">
                        <td className="px-3 py-2 whitespace-nowrap">
                          <button onClick={() => navigate(`/orders/${r.order_id}`)} className="text-white underline decoration-dotted">{r.order_number}</button>
                        </td>
                        <td className="px-3 py-2 text-gray-400 font-mono text-xs whitespace-nowrap">{r.given} → {r.applied}</td>
                        <td className="px-3 py-2 text-gray-400 text-xs">{r.slip}</td>
                        <td className="px-3 py-2 text-gray-500 text-xs whitespace-nowrap">{r.fixed_at ? r.fixed_at.slice(0, 16).replace('T', ' ') : ''}</td>
                        <td className={`px-3 py-2 text-xs whitespace-nowrap ${r.outcome === 'delivered' ? 'text-green-400' : r.outcome === 'returned' || r.outcome === 'refused_again' ? 'text-red-400' : 'text-gray-300'}`}>{r.outcome_text}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <div className="px-4 pb-3 text-xs text-gray-500">None in this period.</div>}
            </Section>
          )}

          {/* Setup issues: our own configuration */}
          {data.setup_issues && data.setup_issues.cases > 0 && (
            <Section title="Setup issues — our settings"
              note="Our own configuration blocked the label (an unmapped method, a missing service or sender, credentials) or the method cannot ship there. Not the parcel's data; saving the fix in Settings releases the blocked orders.">
              <div className="px-4 py-3 text-sm text-gray-300 flex gap-6 flex-wrap">
                <span><span className="text-white font-semibold">{data.setup_issues.cases}</span> cases</span>
                <span><span className="text-green-400 font-semibold">{data.setup_issues.resolved}</span> resolved</span>
                <span><span className={data.setup_issues.open ? 'text-amber-400 font-semibold' : 'text-gray-500'}>{data.setup_issues.open}</span> open</span>
                {Object.entries(data.setup_issues.by_kind).map(([kk, n]) => (
                  <span key={kk} className="text-gray-500">{FIELD_LABEL[kk] || kk}: {n}</span>
                ))}
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {data.setup_issues.recent.map((r, i) => (
                    <tr key={i} className="border-t border-gray-800/50">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <button onClick={() => navigate(`/orders/${r.order_id}`)} className="text-white underline decoration-dotted">{r.order_number}</button>
                      </td>
                      <td className="px-3 py-2 text-gray-400">{CARRIER[r.carrier] || r.carrier}</td>
                      <td className="px-3 py-2 text-gray-400 text-xs">{r.message}</td>
                      <td className={`px-3 py-2 text-xs whitespace-nowrap ${r.resolved ? 'text-green-400' : 'text-amber-400'}`}>{r.resolved ? 'resolved' : 'open'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          {/* Doctor calibration */}
          <Section title="Label Doctor calibration"
            note={`Diagnoses in the period, what the Doctor applied itself, and whether a person ended up doing what it proposed. Mode today: ${data.doctor.mode}${data.doctor.mode === 'apply' ? ' — it may fix verified contact fields and request a re-buy.' : ' — it writes notes, applies nothing.'}`}>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4">
              <KPICard label="Diagnoses" value={String(data.doctor.diagnoses)}
                sub={Object.entries(data.doctor.outcomes).map(([o, n]) => `${n} ${o.replace('_', ' ')}`).join(' · ') || '—'} />
              <KPICard label="Applied by the Doctor" value={String(data.doctor.applied?.total ?? 0)}
                color={data.doctor.applied?.total ? 'green' : undefined}
                sub={data.doctor.applied?.total
                  ? `${data.doctor.applied.bought} bought · ${data.doctor.applied.refused} refused again · ${data.doctor.applied.pending} pending · ${data.doctor.applied.retry_unchanged} retry only · ${Object.entries(data.doctor.applied.fields).map(([f, n]) => `${n} ${FIELD_LABEL[f] || f}`).join(', ') || 'no field changes'}`
                  : 'nothing applied in this period'} />
              <KPICard label="Agreement (person-resolved)"
                value={data.doctor.agreement_total.compared ? `${Math.round(100 * data.doctor.agreement_total.matched / data.doctor.agreement_total.compared)}%` : '—'}
                sub={`${data.doctor.agreement_total.matched} of ${data.doctor.agreement_total.compared} proposed fields matched what a person used`}
                color={data.doctor.agreement_total.compared && data.doctor.agreement_total.matched === data.doctor.agreement_total.compared ? 'green' : undefined} />
              <KPICard label="Outcome of diagnosed orders"
                value={String(data.doctor.resolutions['label_bought'] || 0)}
                sub={'labels bought · ' + Object.entries(data.doctor.resolutions).filter(([r]) => r !== 'label_bought').map(([r, n]) => `${n} ${r.replace('_', ' ')}`).join(' · ')} />
              <KPICard label="Tokens per diagnosis" value={data.doctor.tokens.per_diagnosis.toLocaleString()}
                sub={`${data.doctor.tokens.input.toLocaleString()} in · ${data.doctor.tokens.output.toLocaleString()} out · median ${hrs(data.doctor.median_hours_to_label)} to a label`} />
            </div>
            {Object.keys(data.doctor.agreement).length > 0 && (
              <table className="w-full text-sm">
                <thead><tr className="text-gray-500 text-xs border-b border-gray-800">
                  <th className="px-3 py-2 text-left font-medium">Field the Doctor proposed</th>
                  <th className="px-3 py-2 text-right font-medium">Compared</th>
                  <th className="px-3 py-2 text-right font-medium">Matched what a person used</th>
                  <th className="px-3 py-2 text-right font-medium">Agreement</th>
                </tr></thead>
                <tbody>
                  {Object.entries(data.doctor.agreement).map(([f, a]) => (
                    <tr key={f} className="border-b border-gray-800/50">
                      <td className="px-3 py-2 text-white">{FIELD_LABEL[f] || f}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-300">{a.compared}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-300">{a.matched}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${a.matched === a.compared ? 'text-green-400' : 'text-amber-400'}`}>
                        {a.compared ? `${Math.round(100 * a.matched / a.compared)}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {/* Daily */}
          <Section title="By day" note="Refusals on the day they happened, and who fixed them (the fix may have come on a later day).">
            <table className="w-full text-sm">
              <Head first="Day" />
              <tbody>
                {data.daily.map(d => (
                  <tr key={d.date} className="border-b border-gray-800/50 hover:bg-gray-800/20">
                    <td className="px-3 py-2 text-white whitespace-nowrap">
                      {d.date}
                      <div className="h-1 mt-1 bg-gray-800 rounded"><div className="h-1 bg-blue-500 rounded" style={{ width: `${Math.round(100 * d.refusals / maxDaily)}%` }} /></div>
                    </td>
                    <Cols b={d} />
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* Recent */}
          <Section title="Recent refusals" note="Newest first. Click an order to open it.">
            <table className="w-full text-sm">
              <thead><tr className="text-gray-500 text-xs border-b border-gray-800">
                <th className="px-3 py-2 text-left font-medium">When</th>
                <th className="px-3 py-2 text-left font-medium">Order</th>
                <th className="px-3 py-2 text-left font-medium">Carrier</th>
                <th className="px-3 py-2 text-left font-medium">Field</th>
                <th className="px-3 py-2 text-left font-medium">What the carrier said</th>
                <th className="px-3 py-2 text-left font-medium">Fixed by</th>
                <th className="px-3 py-2 text-right font-medium">Time to fix</th>
              </tr></thead>
              <tbody>
                {data.recent.map((r, i) => (
                  <tr key={i} className="border-b border-gray-800/50 hover:bg-gray-800/20">
                    <td className="px-3 py-2 text-gray-400 whitespace-nowrap">{r.at.slice(0, 16)}</td>
                    <td className="px-3 py-2">
                      <button onClick={() => navigate(`/orders/${r.order_id}`)} className="text-blue-400 hover:underline">{r.order_number}</button>
                      <span className="text-gray-600 text-xs ml-1">{r.path}</span>
                    </td>
                    <td className="px-3 py-2 text-gray-300">{CARRIER[r.carrier] || r.carrier || '—'}</td>
                    <td className="px-3 py-2 text-gray-300">{FIELD_LABEL[r.field] || r.field}</td>
                    <td className="px-3 py-2 text-gray-400 text-xs max-w-md truncate" title={r.message}>{r.message}</td>
                    <td className={`px-3 py-2 ${WHO_COLOR[r.resolved_by] || 'text-gray-300'}`} title={r.used_source || ''}>
                      {WHO_LABEL[r.resolved_by] || r.resolved_by}
                      {r.used_source && <span className="text-gray-600 text-xs ml-1">{r.used_source}</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-300">{hrs(r.hours)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <div className="text-gray-600 text-xs space-y-0.5 mt-4">
            {Object.entries(data.definitions).map(([kk, v]) => (
              <div key={kk}><span className="text-gray-400">{WHO_LABEL[kk] || kk}:</span> {v}</div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Section({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <div className="bg-[#1a1d27] rounded-xl border border-gray-800 mb-5 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-800">
        <h2 className="text-sm font-semibold text-white">{title}</h2>
        {note && <p className="text-gray-500 text-xs mt-0.5">{note}</p>}
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}
